import { Fragment, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { PMPagination } from "../../../utils/PMPagination";
import { TableBody } from "../../../utils/PMTable/TableBody";
import { PMDataTable } from "../../../utils/PMTableView/PMDataTable";
import { DriverStatus } from "./utils";
import { useTranslation } from "react-i18next";
import { axiosWithToast } from "../../../Utils";
import { driverVehicleAction } from "../../../../pages/dashboard/driving/drivers/utils";
import axios from "../../../../api/axios";
import VehiclesListDialog from "../../../../pages/dashboard/vehicles/list-dialog";
import ModernEmployeeSelector from "../../../utils/Selector/ModernEmployeeSelector";
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { ConfirmDialog } from "../../../utils/Prompt";

export const DrivingDriversTable = (props) => {
  const {
    itemState,
    onPageChange,
    itemsCount,
    currentCount,
    page,
    perPage,
    onItemsSelect,
    onRefresh,
  } = props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [checkInDriver, setCheckInDriver] = useState(null);
  const [checkoutDriver, setCheckoutDriver] = useState(null);
  const [linkDriver, setLinkDriver] = useState(null);
  const [selectedLinkEmployees, setSelectedLinkEmployees] = useState([]);

  useLayoutEffect(() => {
    const isIndeterminate = selectedItems.length > 0 && selectedItems.length < data?.length;
    setChecked(selectedItems.length === data?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) checkbox.current.indeterminate = isIndeterminate;
    onItemsSelect(selectedItems);
  }, [selectedItems, data?.length]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : data);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  // Admin driving record: list all active company vehicles (including ones another driver has checked into).
  const checkInVehicleFilters = useMemo(() => [{ name: "active", value: true }], []);

  const refresh = () => {
    if (onRefresh) onRefresh();
  };

  const handleCheckInSelect = async (vehicleId) => {
    if (!checkInDriver?.user?.id) {
      setCheckInDriver(null);
      return;
    }
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: checkInDriver.user.id,
          vehicleId,
          action: "checkin",
        }),
        null,
        null,
        () => refresh()
      );
    } finally {
      setCheckInDriver(null);
    }
  };

  const runDriverCheckout = async () => {
    const d = checkoutDriver;
    const userId = d?.user?.id;
    const driverId = d?.id;
    if (!userId && !driverId) {
      toast.error(t("automile.linkUser"));
      setCheckoutDriver(null);
      return;
    }
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: userId || undefined,
          driverId: userId ? undefined : driverId,
          vehicleId: d?.checked_into_vehicle?.id,
          action: "checkout",
        }),
        null,
        null,
        () => refresh()
      );
    } finally {
      setCheckoutDriver(null);
    }
  };

  const saveLinkedUser = async () => {
    const emp = selectedLinkEmployees[0];
    if (!linkDriver || !emp?.id) return;
    try {
      await axiosWithToast(
        axios.put(`/driving/drivers/${linkDriver.id}`, { driver: { user_id: emp.id } }),
        null,
        null,
        () => refresh()
      );
    } finally {
      setLinkDriver(null);
      setSelectedLinkEmployees([]);
    }
  };

  function renderRows() {
    return data?.map((item) => {
      const canCheckIn =
        item?.user?.id && !(item?.checked_into_vehicle && item.checked_into_vehicle.id);
      const canCheckOut = Boolean(item?.checked_into_vehicle?.id);
      const needsUser = !item?.user?.id;

      return (
        <tr key={item.id}>
          <td className="relative px-7 sm:w-12 sm:px-6">
            {selectedItems.includes(item) && (
              <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-400" />
            )}
            <input
              type="checkbox"
              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
              value={item.id}
              checked={selectedItems.includes(item)}
              onChange={(e) =>
                setSelectedItems(
                  e.target.checked
                    ? [...selectedItems, item]
                    : selectedItems.filter((p) => p !== item)
                )
              }
            />
          </td>
          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
            <div className="flex items-center">
              <div>
                <Link to={"/dashboard/driving/driver" + item?.id} className="text-blue-400">
                  <div className="font-medium">
                    {item.user?.id ? item.user?.displayTitle : item?.name}
                  </div>
                </Link>
              </div>
            </div>
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            <div className="text-gray-500">{item?.checked_into_vehicle?.displayTitle}</div>
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {item?.distance_driven}
            {" km"}
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            <DriverStatus driver={item} />
          </td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
            <div className="flex flex-wrap justify-end gap-2">
              {needsUser && (
                <button
                  type="button"
                  onClick={() => {
                    setLinkDriver(item);
                    setSelectedLinkEmployees([]);
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  {t("automile.linkUser")}
                </button>
              )}
              {canCheckOut && (
                <button
                  type="button"
                  onClick={() => setCheckoutDriver(item)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 text-red-600" />
                  {t("automile.checkOutVehicle")}
                </button>
              )}
              {canCheckIn && (
                <button
                  type="button"
                  onClick={() => setCheckInDriver(item)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeftOnRectangleIcon className="h-4 w-4 text-green-600" />
                  {t("Check in")}
                </button>
              )}
            </div>
          </td>
        </tr>
      );
    });
  }

  if (isLoading) return <ResourceLoading />;

  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="relative">
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
                      ref={checkbox}
                      checked={checked}
                      onChange={toggleAll}
                    />
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    User / Name
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Checked Into Vehicle
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Distance Driven (km)
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                    {t("Vehicle Actions")}
                  </th>
                </tr>
              </thead>
              <TableBody>{!isLoading && data ? renderRows() : ""}</TableBody>
            </table>

            <PMPagination
              disabled={isLoading}
              onPageChange={onPageChange}
              page={page}
              perPage={perPage}
              rowsCount={itemsCount}
              totalCount={itemsCount}
              currentCount={currentCount}
              showCount={true}
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(checkoutDriver)}
        onClose={() => setCheckoutDriver(null)}
        onConfirm={runDriverCheckout}
        title={t("automile.checkoutConfirmTitle")}
        message={t("automile.checkoutConfirmMessage")}
        confirmText={t("automile.checkoutConfirm")}
        cancelText={t("automile.checkoutCancel")}
      />

      <VehiclesListDialog
        open={Boolean(checkInDriver)}
        onClose={() => setCheckInDriver(null)}
        checkInMode
        allowOccupiedVehicles
        filters={checkInVehicleFilters}
        onSelect={handleCheckInSelect}
      />

      <Transition.Root show={Boolean(linkDriver)} as={Fragment}>
        <Dialog as="div" className="relative z-[3600]" onClose={() => setLinkDriver(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 z-[3600] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-t-2xl bg-white shadow-xl sm:rounded-2xl max-h-[min(90vh,100%)] flex flex-col pb-[max(1rem,env(safe-area-inset-bottom))]">
                <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {t("automile.linkUser")}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500">{linkDriver?.name}</p>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden px-2 py-2 sm:px-4">
                  <ModernEmployeeSelector
                    open={Boolean(linkDriver)}
                    selectedEmployees={selectedLinkEmployees}
                    onChange={setSelectedLinkEmployees}
                    onClose={() => setLinkDriver(null)}
                    multiple={false}
                  />
                </div>
                <div className="flex gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
                  <button
                    type="button"
                    className="flex-1 rounded-lg border border-gray-300 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setLinkDriver(null)}
                  >
                    {t("Close")}
                  </button>
                  <button
                    type="button"
                    disabled={!selectedLinkEmployees[0]}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    onClick={saveLinkedUser}
                  >
                    {t("Save")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

DrivingDriversTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
  onRefresh: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// New Promang Table View (schema-driven) implementation for the drivers list.
// ─────────────────────────────────────────────────────────────────────────────

const driverName = (item) =>
  item?.user?.id ? item?.user?.displayTitle : item?.name;

export const driverIsActive = (item) => Boolean(item?.user?.id);

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export const getDriverColumns = (t) => [
  {
    key: "name",
    label: t("automile.userName"),
    sortKey: "name",
    width: 240,
    always: true,
    cell: (item) => (
      <span className="text-[13.5px] font-medium text-slate-800">
        {driverName(item) || "—"}
      </span>
    ),
  },
  {
    key: "checked_into_vehicle",
    label: t("automile.checkedIntoVehicle"),
    width: 200,
    groupable: true,
    groupValue: (item) => item?.checked_into_vehicle?.displayTitle || "",
    groupLabel: (key) => key || t("automile.notCheckedIn"),
    cell: (item) => (
      <span className="block max-w-[200px] truncate text-[13px] text-slate-600">
        {item?.checked_into_vehicle?.displayTitle || "—"}
      </span>
    ),
  },
  {
    key: "distance_driven",
    label: t("automile.distanceDriven"),
    sortKey: "distance_driven",
    width: 150,
    align: "right",
    cell: (item) => (
      <span className="text-[13px] tabular-nums text-slate-600">
        {item?.distance_driven != null ? `${item.distance_driven} km` : "—"}
      </span>
    ),
  },
  {
    key: "status",
    label: t("Status"),
    width: 220,
    stopRowClick: true,
    groupable: true,
    groupValue: (item) => (driverIsActive(item) ? "active" : "no_user"),
    groupLabel: (key) =>
      key === "active" ? t("Aktiv") : t("automile.noUserLinked"),
    cell: (item) => <DriverStatus driver={item} />,
  },
];

export const DriversDataTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect,
  onRowClick,
}) => {
  const { data = [], isLoading } = itemState;
  return (
    <PMDataTable
      columns={columns}
      visibleKeys={visibleKeys}
      widths={widths}
      onResize={onResize}
      groupBy={groupBy}
      sort={sort}
      onSort={onSort}
      data={data}
      isLoading={isLoading}
      density={density}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSelectionChange={onItemsSelect}
      onRowClick={onRowClick}
      minWidth={820}
    />
  );
};

DriversDataTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};
